import { css } from '@emotion/react';
import { t } from 'i18n';
import { useEffect, useRef, useState } from 'react';
import { PassengerCounts } from 'shared';

import { theme } from '../theme';

import { PassengerSpinner } from './PassengerSpinner';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: ${theme.spacing.md};
    min-width: 360px;

    @media (max-width: ${theme.breakpoints.tablet}) {
      min-width: auto;
    }
  `,
  divider: css`
    border-top: 1px solid ${theme.colors.gray[1]};
    margin-top: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.lg};
  `,
  header: css`
    display: flex;
    justify-content: center;
  `,
  spinButtonContainer: css`
    display: flex;
    gap: ${theme.spacing.sm};
    flex-direction: column;
  `,
  buttonContainer: css`
    display: flex;
    gap: ${theme.spacing.md};
    justify-content: end;
  `,
  marginOverride: css`
    margin: 0;
  `,
  buttonLink: css`
    text-decoration: underline;
    color: var(--color-link);
    background: none;
    border: none;
    padding: 0;
    font: var(--font-p);
    text-transform: none;
    min-height: 0;
    min-width: 0;
    font-size: ${theme.fontSize.sm};
  `,
};

type Props = {
  passengers: PassengerCounts;
  onSave: (args: PassengerCounts) => void;
  onClose: () => void;
};

export enum PassengerType {
  Adults = 'adults',
  Children = 'children',
  Infants = 'infants',
}

export const PassengerSelector = ({ passengers, onSave, onClose }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [passengerCounts, setPassengerCounts] = useState({
    adults: passengers.adults,
    children: passengers.children,
    infants: passengers.infants,
    youths: passengers.youths,
  });

  const setPassengerCount = (type: PassengerType) => (value: number) => {
    if (type === PassengerType.Adults && passengerCounts.infants > value) {
      setPassengerCounts(prev => ({
        ...prev,
        adults: value,
        infants: value,
      }));
    } else {
      setPassengerCounts(prev => ({
        ...prev,
        [type]: value,
      }));
    }
  };

  useEffect(() => {
    const clickOutsideHandler = (event: MouseEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', clickOutsideHandler);

    return () => {
      document.removeEventListener('mousedown', clickOutsideHandler);
    };
  }, []);

  return (
    <div css={styles.container} ref={containerRef}>
      <div>
        <div css={styles.header}>
          <h5 css={styles.marginOverride}>{t('common:travelers')}</h5>
        </div>
        <div css={styles.divider} />
        <div css={styles.spinButtonContainer}>
          <PassengerSpinner
            label={t(`common:unit.adult.other`)}
            value={passengerCounts[PassengerType.Adults]}
            onChange={setPassengerCount(PassengerType.Adults)}
            disabledIncrease={passengerCounts.adults + passengerCounts.children === 9}
            disabledDecrease={passengerCounts.adults === 1}
          />

          <PassengerSpinner
            label={t(`common:unit.child.other`)}
            value={passengerCounts[PassengerType.Children]}
            onChange={setPassengerCount(PassengerType.Children)}
            disabledIncrease={passengerCounts.adults + passengerCounts.children === 9}
            disabledDecrease={passengerCounts.children === 0}
          />

          <PassengerSpinner
            label={t(`common:unit.infant.other`)}
            value={passengerCounts[PassengerType.Infants]}
            onChange={setPassengerCount(PassengerType.Infants)}
            disabledIncrease={passengerCounts.infants === passengerCounts.adults}
            disabledDecrease={passengerCounts.infants === 0}
          />
        </div>
        <div css={styles.divider} />
      </div>
      <div>
        <div css={styles.buttonContainer}>
          <button css={styles.buttonLink} onClick={onClose}>
            {t('common:cancel')}
          </button>
          <button onClick={() => onSave(passengerCounts)}> {t('common:save')}</button>
        </div>
      </div>
    </div>
  );
};
